import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { getImageUrl } from '../api';
import { productSlug } from '../utils/slug';

// Inline-styled markdown renderer (no CSS classes needed)
const renderMarkdown = (md) => {
  if (!md) return [];
  const lines = md.split('\n').map(l => l.trim());
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (!line) { i++; continue; }

    // H1
    if (line.startsWith('# ')) {
      elements.push(<h1 key={i} style={{ fontSize: 'clamp(28px,5vw,38px)', fontWeight: 900, margin: '40px 0 16px', letterSpacing: -1, color: '#111', lineHeight: 1.2 }}>{line.slice(2)}</h1>);
    }
    // H2
    else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} style={{ fontSize: 24, fontWeight: 800, margin: '36px 0 14px', color: '#111', borderLeft: '4px solid #4f46e5', paddingLeft: 16, lineHeight: 1.3 }}>{line.slice(3)}</h2>);
    }
    // H3
    else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} style={{ fontSize: 19, fontWeight: 700, margin: '24px 0 10px', color: '#374151' }}>{line.slice(4)}</h3>);
    }
    // Horizontal rule
    else if (line.trim() === '---') {
      elements.push(<div key={i} style={{ border: 'none', borderTop: '2px solid #f3f4f6', margin: '40px 0' }} />);
    }
    // Image
    else if (line.match(/^!\[(.+?)\]\((.+?)\)$/)) {
      const [, alt, src] = line.match(/^!\[(.+?)\]\((.+?)\)$/);
      elements.push(
        <figure key={i} style={{ margin: '28px 0', textAlign: 'center' }}>
          <img src={src} alt={alt} style={{ maxWidth: '100%', width: '100%', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
          {alt && <figcaption style={{ fontSize: 13, color: '#9ca3af', marginTop: 8, fontStyle: 'italic' }}>{alt}</figcaption>}
        </figure>
      );
    }
    // Callout ==text==
    else if (line.match(/^==(.+)==$/)) {
      const text = line.match(/^==(.+)==$/)[1];
      elements.push(
        <div key={i} style={{ background: 'linear-gradient(135deg,#f0f0ff,#e8e8ff)', borderLeft: '4px solid #4f46e5', borderRadius: '0 12px 12px 0', padding: '18px 22px', margin: '28px 0', fontSize: 16, lineHeight: 1.7, color: '#374151', fontStyle: 'italic' }}>
          {text}
        </div>
      );
    }
    // Tip >>text<<
    else if (line.match(/^>>(.+)<<$/)) {
      const text = line.match(/^>>(.+)<<$/)[1];
      elements.push(
        <div key={i} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', margin: '20px 0', fontSize: 15, color: '#92400e' }}>
          <span style={{ marginRight: 6 }}>&#128161;</span>{text}
        </div>
      );
    }
    // Buy button [SHOP: text](url)
    else if (line.startsWith('[SHOP:')) {
      const m = line.match(/\[SHOP:\s*(.+?)\]\((.+?)\)/);
      const text = m ? m[1] : 'Shop Now';
      const url = m ? m[2] : '/shop';
      elements.push(
        <div key={i} style={{ margin: '16px 0' }}>
          <a href={url} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', padding: '11px 24px', borderRadius: 100, fontWeight: 700, fontSize: 14, textDecoration: 'none', boxShadow: '0 4px 12px rgba(79,70,229,0.3)' }}>
            &#128722; {text} &rarr;
          </a>
        </div>
      );
    }
    // Bullet list
    else if (line.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
          {items.map((item, j) => (
            <li key={j} style={{ padding: '7px 0 7px 28px', position: 'relative', fontSize: 17, lineHeight: 1.7, color: '#374151' }}>
              <span style={{ position: 'absolute', left: 0, color: '#4f46e5', fontWeight: 700 }}>&#10022;</span>
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    }
    // Normal paragraph
    else {
      elements.push(<p key={i} style={{ fontSize: 17, lineHeight: 1.85, color: '#374151', margin: '0 0 18px' }}>{renderInline(line)}</p>);
    }
    i++;
  }
  return elements;
};

const renderInline = (text) => {
  // Handle bold, italic, and links inline
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|\[(.+?)\]\((.+?)\))/g;
  let last = 0, match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[0].startsWith('**')) parts.push(<strong key={match.index}>{match[2]}</strong>);
    else if (match[0].startsWith('*')) parts.push(<em key={match.index}>{match[3]}</em>);
    else parts.push(<a key={match.index} href={match[5]} style={{ color: '#4f46e5', textDecoration: 'underline', textUnderlineOffset: 3 }}>{match[4]}</a>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => {
      setPost(r.data);
      const tag = r.data.tags?.split(',')[0]?.trim() || 'jewelry';
      api.get(`/products?search=${encodeURIComponent(tag)}&limit=4`).then(pr => setRelatedProducts(pr.data.products || [])).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container" style={{ textAlign: 'center', padding: 80 }}>
      <h2>Post not found</h2>
      <Link to="/blog" className="btn btn-primary" style={{ marginTop: 16 }}>Back to Blog</Link>
    </div>
  );

  if (!post) return <div className="spinner" />;

  const readTime = Math.max(1, Math.ceil((post.content?.length || 0) / 1000));

  return (
    <div>
      {/* Hero */}
      <div style={{ background: post.cover_image ? `url(${post.cover_image}) center/cover` : 'linear-gradient(135deg,#1e1b4b,#4c1d95)', minHeight: 400, display: 'flex', alignItems: 'flex-end', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 860, margin: '0 auto', padding: '0 24px 48px', width: '100%', color: 'white' }}>
          {post.tags && (
            <div style={{ marginBottom: 14 }}>
              {post.tags.split(',').slice(0,3).map(t => (
                <span key={t} style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: 100, padding: '4px 12px', marginRight: 8, fontWeight: 600 }}>{t.trim()}</span>
              ))}
            </div>
          )}
          <h1 style={{ fontSize: 'clamp(26px,5vw,46px)', fontWeight: 900, lineHeight: 1.15, marginBottom: 16, letterSpacing: -1 }}>{post.title}</h1>
          {post.excerpt && <p style={{ fontSize: 18, opacity: 0.9, lineHeight: 1.6, marginBottom: 20, maxWidth: 620 }}>{post.excerpt}</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 14, opacity: 0.8 }}>
            <span style={{ fontWeight: 700 }}>{post.author}</span>
            <span>&middot;</span>
            <span>{post.published_at ? new Date(post.published_at).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : ''}</span>
            <span>&middot;</span>
            <span>{readTime} min read</span>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={{ borderBottom: '1px solid #e5e7eb', padding: '12px 0' }}>
        <div className="container" style={{ maxWidth: 860, fontSize: 13, color: '#9ca3af' }}>
          <Link to="/blog" style={{ color: '#4f46e5' }}>Blog</Link> &rsaquo; {post.title}
        </div>
      </div>

      {/* Post content */}
      <div className="container" style={{ maxWidth: 800, paddingTop: 48, paddingBottom: 40 }}>
        {renderMarkdown(post.content)}
      </div>

      {/* Discount CTA */}
      <div style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', padding: '40px 24px', textAlign: 'center', margin: '0 0 48px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>&#127873;</div>
          <h3 style={{ fontSize: 24, fontWeight: 900, marginBottom: 10 }}>Ready to shop these looks?</h3>
          <p style={{ opacity: 0.9, marginBottom: 24, fontSize: 16 }}>
            Use code <strong style={{ fontSize: 20, letterSpacing: 2, background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '2px 12px', marginLeft: 4, marginRight: 4 }}>WELCOME10</strong> for 10% off your first order.
          </p>
          <Link to="/shop" style={{ background: 'white', color: '#4f46e5', padding: '14px 36px', borderRadius: 100, fontWeight: 800, fontSize: 15, textDecoration: 'none', display: 'inline-block', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>Shop Now &rarr;</Link>
        </div>
      </div>

      {/* Shop The Look */}
      {relatedProducts.length > 0 && (
        <div className="container" style={{ maxWidth: 1100, paddingBottom: 60 }}>
          <h2 style={{ fontSize: 26, fontWeight: 900, marginBottom: 8 }}>Shop The Look</h2>
          <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 15 }}>Products featured in this article</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 20 }}>
            {relatedProducts.map(p => {
              const thumb = p.images?.[0]?.image_url || p.image_url;
              return (
                <Link key={p.id} to={`/product/${productSlug(p.name, p.id)}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden', transition: 'all 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'none'; }}>
                    {thumb ? (
                      <img src={getImageUrl(thumb)} alt={p.name} style={{ width: '100%', height: 220, objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: 220, background: 'linear-gradient(135deg,#f0f0ff,#e8e8ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>&#128142;</div>
                    )}
                    <div style={{ padding: '14px 16px 18px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, lineHeight: 1.4, color: '#374151' }}>{p.name}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: '#4f46e5' }}>${p.price?.toFixed(2)}</div>
                        <span style={{ fontSize: 12, background: '#4f46e5', color: 'white', borderRadius: 100, padding: '4px 12px', fontWeight: 600 }}>Shop</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid #e5e7eb', padding: '32px 0', textAlign: 'center' }}>
        <Link to="/blog" style={{ color: '#4f46e5', fontWeight: 700, textDecoration: 'none', fontSize: 15 }}>&larr; Back to Blog</Link>
      </div>
    </div>
  );
}
